import React, { useEffect, useState } from "react";
import { Bell, ChevronRight, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import AlertDrawer from "./AlertDrawer";
import CreateAlertDialog from "./CreateAlertDialog";
import { AlertSeverityBadge, UrgencyMeter } from "./AlertBadges";

/**
 * Compact banner shown at the top of a document's Analysis page when one or
 * more non-terminal alerts reference that document. Clicking a row opens the
 * full AlertDrawer.
 *
 * Statuses included: active, snoozed, accepted_risk, muted (anything
 * still "live"). Resolved and superseded are excluded — they're history.
 */
const LIVE_STATUSES = new Set(["active", "snoozed", "accepted_risk", "muted"]);

export default function AlertsBanner({ documentId }) {
  const { t } = useTranslation("components");
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [reminderOpen, setReminderOpen] = useState(false);

  const fetchAlerts = async () => {
    if (!documentId) return;
    setLoading(true);
    try {
      // Fetch the 4 "live" statuses in parallel for this document.
      const statuses = ["active", "snoozed", "accepted_risk", "muted"];
      const responses = await Promise.all(
        statuses.map((status) =>
          api.get("/alm/alerts", {
            params: { document_id: documentId, status, limit: 50, sort: "urgency" },
          })
        )
      );
      const all = responses.flatMap((r) => r.data.items || []);
      // Sort by urgency desc
      all.sort((a, b) => (b.urgency_score || 0) - (a.urgency_score || 0));
      setAlerts(all.filter((a) => LIVE_STATUSES.has(a.status)));
    } catch (e) {
      // Silent failure — banner is non-essential
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAlerts(); }, [documentId]);

  if (loading) return null;

  // When there are no alerts we still want the "+ Rappel" affordance so
  // the user can create the very first reminder for this document.
  const hasAlerts = alerts.length > 0;
  const top = alerts.slice(0, collapsed ? 0 : 3);
  const more = alerts.length - top.length;

  const createReminderButton = (
    <button
      onClick={() => setReminderOpen(true)}
      className="text-[11px] text-amber-900 hover:underline inline-flex items-center gap-1"
      data-testid="create-reminder-button"
    >
      <Plus size={11} strokeWidth={2.25} />
      {t("alerts_banner.create_reminder")}
    </button>
  );

  // Minimal affordance when the doc has no live alert yet
  if (!hasAlerts) {
    return (
      <>
        <section
          className="mb-4 border border-zinc-200 bg-zinc-50/40 rounded-xl px-4 py-2.5 flex items-center justify-between"
          data-testid="alerts-banner-empty"
        >
          <span className="text-xs text-zinc-500 inline-flex items-center gap-2">
            <Bell size={12} strokeWidth={2} className="text-zinc-400" />
            {t("alerts_banner.empty_hint")}
          </span>
          <button
            onClick={() => setReminderOpen(true)}
            className="text-[11px] text-zinc-800 hover:underline inline-flex items-center gap-1"
            data-testid="create-reminder-button"
          >
            <Plus size={11} strokeWidth={2.25} />
            {t("alerts_banner.create_reminder")}
          </button>
        </section>
        <CreateAlertDialog
          open={reminderOpen}
          onClose={() => setReminderOpen(false)}
          onCreated={() => fetchAlerts()}
          documentId={documentId}
          variant="reminder"
        />
      </>
    );
  }

  return (
    <section
      className="mb-4 border border-amber-300/60 bg-amber-50/40 rounded-xl overflow-hidden"
      data-testid="alerts-banner"
    >
      <header className="flex items-center justify-between px-4 py-2.5 border-b border-amber-200/60 bg-amber-100/30">
        <div className="flex items-center gap-2">
          <Bell size={14} className="text-amber-800" strokeWidth={2} />
          <span
            className="text-xs font-semibold text-amber-950 uppercase tracking-wider"
            data-testid="alerts-banner-count"
          >
            {t("alerts_banner.open_alerts", { count: alerts.length })}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {createReminderButton}
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="text-[11px] text-amber-900 hover:underline"
            data-testid="alerts-banner-toggle"
          >
            {collapsed ? t("alerts_banner.show") : t("alerts_banner.hide")}
          </button>
          <button
            onClick={() => navigate(`/alerts?document_id=${documentId}`)}
            className="text-[11px] text-amber-900 hover:underline inline-flex items-center gap-0.5"
            data-testid="alerts-banner-view-all"
          >
            {t("alerts_banner.open_center")} <ChevronRight size={10} />
          </button>
        </div>
      </header>

      {!collapsed && (
        <ul className="divide-y divide-amber-100">
          {top.map((a) => (
            <li key={a.id}>
              <button
                onClick={() => setSelectedId(a.id)}
                className="w-full text-left px-4 py-2.5 hover:bg-amber-50 transition-colors flex items-center gap-3"
                data-testid={`banner-alert-${a.id}`}
              >
                <AlertSeverityBadge severity={a.severity} className="shrink-0" />
                <span className="flex-1 text-sm text-zinc-950 truncate">{a.title}</span>
                <UrgencyMeter score={a.urgency_score} signals={a.urgency_signals} compact />
              </button>
            </li>
          ))}
          {more > 0 && (
            <li className="px-4 py-2 text-xs text-amber-900 bg-amber-50/30">
              {t("alerts_banner.more", { count: more })} — <button
                  onClick={() => navigate(`/alerts?document_id=${documentId}`)}
                  className="underline"
                  data-testid="alerts-banner-show-more"
                >{t("alerts_banner.view_in_center")}</button>
            </li>
          )}
        </ul>
      )}

      {selectedId && (
        <AlertDrawer
          alertId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={fetchAlerts}
        />
      )}

      <CreateAlertDialog
        open={reminderOpen}
        onClose={() => setReminderOpen(false)}
        onCreated={() => fetchAlerts()}
        documentId={documentId}
        variant="reminder"
      />
    </section>
  );
}
